import API_BASE from '../config';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CreditCard, Shield, Clock, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { detectCountryFromInput } from '../utils/phoneCountries';
import PhoneCountryPicker from '../components/PhoneCountryPicker';
import { getTrackedEvaluationId, trackEvent } from '../services/journeyTracking';
import { getTrackedUtm } from '../services/utmTracking';

// Payment session hard-caps at 10 minutes — matches WhatsApp abandoned-reminder threshold
const PAYMENT_SESSION_LIMIT_SECONDS = 30 * 60;

const API_URL = API_BASE;

// Official brand logo asset (frontend/public/images/brand/) — same file
// used verbatim on every screen. Only `height` is set; width stays `auto`
// so the image's own aspect ratio is preserved and never distorted.
const Logo = ({ height = 22 }) => (
  <img
    src="/images/brand/successlane-logo-mark.png"
    alt="SuccessLane"
    className="sl-logo"
    style={{ height, width: 'auto', display: 'block' }}
  />
);

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [indianStates, setIndianStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [pricing, setPricing] = useState({
    price: 999, standard_price: 999, offer_price: null, is_offer_active: false,
    market_price: null, is_market_active: false, compare_at_price: null,
  });
  const [showCoupon, setShowCoupon] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponApplied, setCouponApplied] = useState(null); // {code, discounted_price, savings, discount_type, discount_value}
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');

  // ── 10-minute payment session timeout ────────────────────────────────────
  const [sessionActive, setSessionActive] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(PAYMENT_SESSION_LIMIT_SECONDS);
  const [sessionExpired, setSessionExpired] = useState(false);
  const txIdRef = useRef(null);
  const rzpInstanceRef = useRef(null);
  const timerRef = useRef(null);
  const sessionDoneRef = useRef(false); // set true when payment completes/fails/cancels

  const clearSessionTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const notifyBackendTimeout = async (txId) => {
    if (!txId) return;
    try {
      await fetch(`${API_URL}/api/checkout/timeout/${encodeURIComponent(txId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch (e) {
      console.warn('checkout timeout notification failed:', e);
    }
  };

  const handleSessionExpired = () => {
    if (sessionDoneRef.current) return;
    sessionDoneRef.current = true;
    clearSessionTimer();
    // Close the Razorpay modal if it's still open
    try {
      if (rzpInstanceRef.current && typeof rzpInstanceRef.current.close === 'function') {
        rzpInstanceRef.current.close();
      }
    } catch (_) {}
    setSessionExpired(true);
    setSessionActive(false);
    setLoading(false);
    setRedirecting(false);
    notifyBackendTimeout(txIdRef.current);
  };

  const startSessionTimer = () => {
    sessionDoneRef.current = false;
    setSessionExpired(false);
    setSessionActive(true);
    setSecondsRemaining(PAYMENT_SESSION_LIMIT_SECONDS);
    clearSessionTimer();
    timerRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          handleSessionExpired();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const endSessionTimer = () => {
    sessionDoneRef.current = true;
    clearSessionTimer();
    setSessionActive(false);
  };

  useEffect(() => () => clearSessionTimer(), []);

  // Funnel telemetry. Distinguishes "reached checkout" from "clicked Pay" —
  // without it, everyone who lands here and leaves before hitting the payment
  // button is invisible, since payment_transactions only gets a row once
  // Razorpay has already been asked for an order.
  useEffect(() => { trackEvent('checkout_viewed'); }, []);

  const [formData, setFormData] = useState({
    // Prefilled from query params when the customer returns via the WhatsApp resume link
    customer_name:    searchParams.get('name')    || '',
    customer_email:   searchParams.get('email')   || '',
    customer_phone:   searchParams.get('phone')   || '',
    customer_country: searchParams.get('country') || 'India',
    customer_state:   searchParams.get('state')   || '',
  });

  // Phone split into country dial + local number for the picker UI.
  // We initialise from the prefilled customer_phone (resume link) or default to India.
  const _initialPhone = detectCountryFromInput(searchParams.get('phone') || '', '+91');
  const [phoneCountryDial, setPhoneCountryDial] = useState(_initialPhone.countryDial);
  const [phoneLocal, setPhoneLocal] = useState(_initialPhone.localNumber);

  // Keep formData.customer_phone in sync as either sub-field changes (E.164: +91XXXXXXXXXX)
  useEffect(() => {
    const full = phoneLocal ? `${phoneCountryDial}${phoneLocal}` : '';
    setFormData(prev => ({ ...prev, customer_phone: full }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneCountryDial, phoneLocal]);

  const handlePhoneChange = (rawValue) => {
    // Extract digits + possible leading +. If customer types/pastes with a
    // + prefix (e.g. +919003454300), auto-detect country and split.
    const parsed = detectCountryFromInput(rawValue, phoneCountryDial);
    if (parsed.countryDial !== phoneCountryDial) {
      setPhoneCountryDial(parsed.countryDial);
    }
    setPhoneLocal(parsed.localNumber);
  };

  useEffect(() => {
    fetchLocations();
    fetch(`${API_URL}/api/pricing/current`)
      .then(r => r.json())
      .then(d => setPricing(d))
      .catch(() => {});

    // If the customer returned via the WhatsApp resume link with a coupon,
    // silently re-apply it so the price + Pay button reflect the discount.
    const resumeCoupon = searchParams.get('coupon');
    if (resumeCoupon) {
      setCouponInput(resumeCoupon.toUpperCase());
      setShowCoupon(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLocations = async () => {
    try {
      const [countriesRes, statesRes] = await Promise.all([
        fetch(`${API_URL}/api/location/countries`),
        fetch(`${API_URL}/api/location/indian-states`)
      ]);
      const countriesData = await countriesRes.json();
      const statesData = await statesRes.json();
      setCountries(countriesData);
      setIndianStates(statesData);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Please enter your full name';
    }
    
    if (!formData.customer_email.trim()) {
      newErrors.customer_email = 'Please enter your email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email)) {
      newErrors.customer_email = 'Please enter a valid email';
    }
    
    if (!phoneLocal.trim()) {
      newErrors.customer_phone = 'Please enter your WhatsApp number.';
    } else if (phoneLocal.replace(/\D/g, '').length < 6) {
      newErrors.customer_phone = 'That number looks too short — please check.';
    }
    
    if (!formData.customer_country) {
      newErrors.customer_country = 'Please select your country';
    }
    
    if (formData.customer_country === 'India' && !formData.customer_state) {
      newErrors.customer_state = 'Please select your state';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Lazy-load Razorpay checkout script from CDN on first need
  const loadRazorpayScript = () => new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve(true);
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load Razorpay'));
    document.body.appendChild(script);
  });

  const startRazorpayCheckout = async () => {
    // Backend creates the order; returns order id + amount + key id
    const utm = getTrackedUtm();
    const orderRes = await fetch(`${API_URL}/api/payments/razorpay/create-order`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone || '',
        customer_country: formData.customer_country,
        customer_state: formData.customer_state || '',
        coupon_code: couponApplied ? couponApplied.code : null,
        // Carries free-eval attribution into the paid order. Null for people
        // who bought without running an evaluation first — which is itself a
        // number worth knowing.
        evaluation_id: getTrackedEvaluationId(),
        // First-touch UTM campaign attribution (null for organic/direct).
        utm_source: utm.source,
        utm_campaign: utm.campaign,
        utm_term: utm.term,
      }),
    });
    const order = await orderRes.json();
    if (!orderRes.ok) throw new Error(order.detail || 'Could not create Razorpay order');

    // Store the tx_id so the timeout endpoint knows which session to reminder
    txIdRef.current = order.tx_id || null;
    // Start the 10-minute session timer now (Razorpay modal is about to open)
    startSessionTimer();

    await loadRazorpayScript();

    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Successlane',
        description: 'LinkedIn Profile Rewrite — recruiter-certified',
        order_id: order.razorpay_order_id,
        prefill: {
          name: order.customer_name,
          email: order.customer_email,
          contact: order.customer_phone || '',
        },
        theme: { color: '#7c3aed' },
        modal: {
          ondismiss: () => {
            // User closed the Razorpay modal without paying.
            // Leave the session timer running — if they don't retry within
            // 10 min, the backend will send the WhatsApp reminder.
            setLoading(false);
            setRedirecting(false);
            reject(new Error('Payment cancelled'));
          },
        },
        handler: async (response) => {
          try {
            const verifyRes = await fetch(`${API_URL}/api/payments/razorpay/verify`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });
            const verifyData = await verifyRes.json();
            if (!verifyRes.ok || !verifyData.success) {
              throw new Error(verifyData.detail || 'Payment verification failed');
            }
            // Payment succeeded — stop timer, no reminder needed.
            endSessionTimer();
            setRedirecting(true);
            // .replace(), not .href= — a real payment just went through, so
            // /checkout should never be reachable again via the back button
            // (re-triggering a paid checkout is exactly the kind of thing
            // that shouldn't be one back-press away). Also keeps this
            // consistent with the rest of the funnel: back collapses to the
            // landing page instead of threading back through every step.
            window.location.replace(`/payment-success?order_id=${encodeURIComponent(verifyData.order_id)}&provider=razorpay`);
            resolve();
          } catch (e) {
            reject(e);
          }
        },
      });
      rzp.on('payment.failed', (resp) => {
        reject(new Error(resp?.error?.description || 'Payment failed'));
      });
      rzpInstanceRef.current = rzp;
      rzp.open();
    });
  };

  const effectivePrice = couponApplied ? couponApplied.discounted_price : pricing.price;

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, base_price: pricing.price }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid coupon');
      setCouponApplied({ code, ...data });
      setCouponInput('');
    } catch (err) {
      setCouponError(err.message || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponInput('');
    setCouponError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setRedirecting(false);

    try {
      await startRazorpayCheckout();
    } catch (err) {
      console.error('Checkout error:', err);
      alert(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
      setRedirecting(false);
    }
  };

  return (
    <div className="checkout-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap');

        :root {
          --blue: #7c3aed;
          --blue-light: #f6f4fd;
          --blue-dark: #6d28d9;
          --navy: #1a0c38;
          --success: #16a34a;
          --success-bg: #dcfce7;
          --slate-50: #f8fafc;
          --slate-100: #f1f5f9;
          --slate-200: #e2e8f0;
          --slate-300: #cbd5e1;
          --slate-400: #94a3b8;
          --slate-500: #64748b;
          --slate-600: #475569;
          --slate-700: #334155;
          --slate-900: #0f172a;
          --danger: #dc2626;
        }

        * { box-sizing: border-box; }

        .checkout-page, .checkout-page * {
          font-family: 'Poppins', sans-serif;
        }

        .checkout-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f7fc 0%, #f6f4fd 100%);
        }

        /* Full page redirect overlay */
        .redirect-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, #f8f7fc 0%, #f6f4fd 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }
        .redirect-card {
          background: white;
          border-radius: 20px;
          padding: 48px;
          text-align: center;
          box-shadow: 0 20px 60px rgba(26, 111, 214, 0.15);
          max-width: 400px;
        }
        .redirect-spinner {
          width: 56px;
          height: 56px;
          border: 4px solid var(--blue-light);
          border-top-color: var(--blue);
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin: 0 auto 24px;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        .redirect-title {
          font-size: 20px;
          font-weight: 700;
          color: var(--navy);
          margin-bottom: 8px;
        }
        .redirect-text {
          font-size: 14px;
          color: var(--slate-500);
          line-height: 1.5;
        }
        .redirect-secure {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin-top: 20px;
          font-size: 12px;
          color: var(--success);
          font-weight: 500;
        }
        .redirect-secure svg {
          width: 14px;
          height: 14px;
        }
        
        .checkout-header {
          background: white;
          border-bottom: 1px solid var(--slate-200);
          padding: 16px 24px;
          position: sticky;
          top: 0;
          z-index: 100;
        }
        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .back-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--slate-600);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          background: none;
          border: none;
          padding: 8px 12px;
          border-radius: 8px;
          transition: all 0.15s;
        }
        .back-btn:hover {
          background: var(--slate-100);
          color: var(--slate-900);
        }
        .logo {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .secure-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--success);
          font-weight: 500;
        }
        
        .checkout-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 40px 24px;
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 40px;
        }
        
        @media (max-width: 900px) {
          .checkout-container {
            grid-template-columns: 1fr;
            padding: 24px 16px;
            gap: 24px;
          }
          .order-summary {
            order: -1;
            /* .order-summary is position:sticky;top:100px for the desktop
               2-column layout, so it stays pinned in its own column while
               the (usually taller) form scrolls beside it — a normal sticky-
               sidebar pattern. Once the grid collapses to a single column
               here, that's no longer two side-by-side columns — it's one
               stack — so the "sticky sidebar" instead pins the order-summary
               card near the top of the viewport while the name/email/phone
               fields (right after it in the stack) scroll up underneath it,
               hidden behind it. Sticky only makes sense with a taller
               sibling next to it; reset it to a normal block once there's
               no longer a sibling column for it to float alongside. */
            position: static !important;
            top: auto !important;
          }
        }

        @media (max-width: 640px) {
          .checkout-header {
            padding: 12px 14px;
          }
          .header-content {
            flex-wrap: wrap;
            gap: 8px;
          }
          .back-btn {
            font-size: 13px;
            padding: 6px 8px;
          }
          .secure-badge {
            display: none;
          }
          [data-testid="checkout-timer"] {
            font-size: 11px !important;
            padding: 4px 10px !important;
          }
          .form-section, .order-summary {
            padding: 20px 18px;
            border-radius: 14px;
          }
          .form-title {
            font-size: 20px;
          }
          .redirect-card {
            padding: 32px 24px;
            max-width: 92vw;
          }
        }
        
        /* Form Section */
        .form-section {
          background: white;
          border-radius: 16px;
          padding: 32px;
          border: 1px solid var(--slate-200);
        }
        .form-title {
          font-size: 24px;
          font-weight: 700;
          color: var(--navy);
          margin-bottom: 8px;
        }
        .form-subtitle {
          font-size: 14px;
          color: var(--slate-500);
          margin-bottom: 28px;
        }
        
        .field {
          margin-bottom: 20px;
        }
        .field label {
          display: block;
          font-size: 13px;
          font-weight: 600;
          color: var(--slate-700);
          margin-bottom: 6px;
        }
        .field-required {
          color: var(--danger);
        }
        
        input, select {
          width: 100%;
          padding: 12px 14px;
          border: 1px solid var(--slate-300);
          border-radius: 10px;
          font-size: 15px;
          color: var(--slate-900);
          background: white;
          transition: all 0.15s;
        }
        input:focus, select:focus {
          outline: none;
          border-color: var(--blue);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
        }
        input.error, select.error {
          border-color: var(--danger);
        }
        .field-error {
          font-size: 12px;
          color: var(--danger);
          margin-top: 4px;
        }
        
        .field-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        @media (max-width: 500px) {
          .field-row {
            grid-template-columns: 1fr;
          }
        }
        
        .pay-btn {
          width: 100%;
          padding: 16px 24px;
          background: linear-gradient(135deg, #8b5cf6, #7c3aed);
          color: white;
          border: none;
          border-radius: 12px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.15s;
          margin-top: 28px;
        }
        .pay-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 8px 24px rgba(124, 58, 237, 0.4);
        }
        .pay-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .pay-btn .spinner {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .trust-badges {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          margin-top: 20px;
          padding-top: 20px;
          border-top: 1px solid var(--slate-100);
        }
        .trust-badge {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          color: var(--slate-500);
        }
        .trust-badge svg {
          width: 16px;
          height: 16px;
          color: var(--slate-400);
        }
        
        /* Order Summary */
        .order-summary {
          background: white;
          border-radius: 16px;
          padding: 28px;
          border: 1px solid var(--slate-200);
          height: fit-content;
          position: sticky;
          top: 100px;
        }
        .summary-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--navy);
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--slate-100);
        }
        
        .product-item {
          display: flex;
          gap: 16px;
          margin-bottom: 20px;
        }
        .product-icon {
          width: 56px;
          height: 56px;
          background: var(--blue-light);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .product-icon svg {
          width: 28px;
          height: 28px;
          color: var(--blue);
        }
        .product-info {
          flex: 1;
        }
        .product-name {
          font-size: 15px;
          font-weight: 600;
          color: var(--slate-900);
          margin-bottom: 4px;
        }
        .product-desc {
          font-size: 12px;
          color: var(--slate-500);
        }
        
        .price-breakdown {
          padding: 16px 0;
          border-top: 1px solid var(--slate-100);
          border-bottom: 1px solid var(--slate-100);
          margin-bottom: 16px;
        }
        .price-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          color: var(--slate-600);
          margin-bottom: 8px;
        }
        .price-row:last-child {
          margin-bottom: 0;
        }
        .price-row.total {
          font-size: 18px;
          font-weight: 700;
          color: var(--navy);
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px dashed var(--slate-200);
        }
        
        .features-list {
          margin-top: 20px;
        }
        .features-title {
          font-size: 12px;
          font-weight: 600;
          color: var(--slate-500);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 12px;
        }
        .feature-item {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 13px;
          color: var(--slate-700);
          margin-bottom: 10px;
        }
        .feature-item svg {
          width: 16px;
          height: 16px;
          color: var(--success);
          flex-shrink: 0;
        }
        
        .guarantee-box {
          background: var(--success-bg);
          border: 1px solid #a7f3d0;
          border-radius: 10px;
          padding: 14px;
          margin-top: 20px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .guarantee-box svg {
          width: 20px;
          height: 20px;
          color: var(--success);
          flex-shrink: 0;
          margin-top: 2px;
        }
        .guarantee-text {
          font-size: 12px;
          color: #065f46;
          line-height: 1.5;
        }
        .guarantee-text strong {
          display: block;
          font-size: 13px;
          margin-bottom: 2px;
        }
      `}</style>

      {/* Full Page Redirect Overlay */}
      {(loading || redirecting) && !sessionExpired && (
        <div className="redirect-overlay">
          <div className="redirect-card">
            <div className="redirect-spinner"></div>
            <h2 className="redirect-title">
              {redirecting ? 'Redirecting to Razorpay...' : 'Creating your order...'}
            </h2>
            <p className="redirect-text">
              {redirecting
                ? 'You\'ll be taken to a secure Razorpay checkout page to complete your payment.'
                : 'Please wait while we set up your secure checkout session.'
              }
            </p>
            <div className="redirect-secure">
              <Shield />
              256-bit SSL Encrypted
            </div>
          </div>
        </div>
      )}

      {/* Session Expired Overlay */}
      {sessionExpired && (
        <div className="redirect-overlay" data-testid="session-expired-overlay">
          <div className="redirect-card">
            <div style={{ fontSize: 40, marginBottom: 12 }}>⏱️</div>
            <h2 className="redirect-title">Session expired</h2>
            <p className="redirect-text">
              Your 10-minute payment window has ended. No worries — we've saved your details.
              Try again to complete your purchase, or check WhatsApp for a resume link we may have sent.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              style={{
                marginTop: 20, padding: '12px 24px', background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', color: '#fff',
                border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer',
              }}
              data-testid="try-again-btn"
            >
              Try again
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="checkout-header">
        <div className="header-content">
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={18} />
            Back
          </button>
          <div className="logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <Logo />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {sessionActive && !sessionExpired && (
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '5px 12px', borderRadius: 20,
                  background: secondsRemaining <= 60 ? '#fef2f2' : '#f6f4fd',
                  color:      secondsRemaining <= 60 ? '#b91c1c' : '#6d28d9',
                  border:     `1px solid ${secondsRemaining <= 60 ? '#fecaca' : '#ddd6fe'}`,
                  fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
                }}
                data-testid="checkout-timer"
              >
                <Clock size={13} />
                {Math.floor(secondsRemaining / 60)}:{String(secondsRemaining % 60).padStart(2, '0')} left
              </div>
            )}
            <div className="secure-badge">
              <Shield size={14} />
              Secure Checkout
            </div>
          </div>
        </div>
      </header>

      <div className="checkout-container">
        {/* Form Section */}
        <div className="form-section">
          <h1 className="form-title">Complete Your Order</h1>
          <p className="form-subtitle">Enter your details to proceed to secure payment</p>

          <form onSubmit={handleSubmit}>
            <div className="field">
              <label>Full Name <span className="field-required">*</span></label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                placeholder="John Doe"
                className={errors.customer_name ? 'error' : ''}
                data-testid="checkout-name"
              />
              {errors.customer_name && <div className="field-error">{errors.customer_name}</div>}
            </div>

            <div className="field">
              <label>Email Address <span className="field-required">*</span></label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({...formData, customer_email: e.target.value})}
                placeholder="john@example.com"
                className={errors.customer_email ? 'error' : ''}
                data-testid="checkout-email"
              />
              {errors.customer_email && <div className="field-error">{errors.customer_email}</div>}
            </div>

            <div className="field">
              <label>WhatsApp Number <span className="field-required">*</span></label>
              <div style={{ display: 'flex', gap: 8 }}>
                <PhoneCountryPicker
                  value={phoneCountryDial}
                  onChange={setPhoneCountryDial}
                  testId="checkout-phone-country"
                />
                <input
                  type="tel"
                  value={phoneLocal}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  onPaste={(e) => {
                    const paste = (e.clipboardData || window.clipboardData).getData('text');
                    if (paste && paste.trim().startsWith('+')) {
                      e.preventDefault();
                      handlePhoneChange(paste);
                    }
                  }}
                  placeholder="9876543210"
                  className={errors.customer_phone ? 'error' : ''}
                  data-testid="checkout-phone"
                  style={{ flex: 1, minWidth: 0 }}
                  inputMode="numeric"
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--slate-500)', marginTop: 4 }}>
                Choose your country, then enter your number (digits only). Or paste in full international format like <code>+919876543210</code> — we'll auto-detect the country.
              </div>
              {errors.customer_phone && <div className="field-error">{errors.customer_phone}</div>}
            </div>

            <div className="field-row">
              <div className="field">
                <label>Country <span className="field-required">*</span></label>
                <select
                  value={formData.customer_country}
                  onChange={(e) => setFormData({...formData, customer_country: e.target.value, customer_state: ''})}
                  className={errors.customer_country ? 'error' : ''}
                  data-testid="checkout-country"
                >
                  <option value="">Select country</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.name}>{c.name}</option>
                  ))}
                </select>
                {errors.customer_country && <div className="field-error">{errors.customer_country}</div>}
              </div>

              {formData.customer_country === 'India' && (
                <div className="field">
                  <label>State <span className="field-required">*</span></label>
                  <select
                    value={formData.customer_state}
                    onChange={(e) => setFormData({...formData, customer_state: e.target.value})}
                    className={errors.customer_state ? 'error' : ''}
                    data-testid="checkout-state"
                  >
                    <option value="">Select state</option>
                    {indianStates.map(s => (
                      <option key={s.code} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                  {errors.customer_state && <div className="field-error">{errors.customer_state}</div>}
                </div>
              )}
            </div>

            {/* Coupon Code */}
            <div style={{ marginTop: 4, marginBottom: 4 }}>
              {!couponApplied ? (
                <>
                  <button
                    type="button"
                    onClick={() => setShowCoupon(v => !v)}
                    style={{ background: 'none', border: 'none', color: 'var(--blue)', fontSize: 13, cursor: 'pointer', padding: 0, fontWeight: 500 }}
                  >
                    {showCoupon ? 'Hide coupon' : 'Have a coupon code?'}
                  </button>
                  {showCoupon && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <input
                        type="text"
                        value={couponInput}
                        onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), applyCoupon())}
                        placeholder="Enter coupon code"
                        style={{ flex: 1, padding: '10px 12px', border: `1px solid ${couponError ? 'var(--danger)' : 'var(--slate-300)'}`, borderRadius: 8, fontSize: 14, outline: 'none' }}
                      />
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={couponLoading || !couponInput.trim()}
                        style={{ padding: '10px 16px', background: 'var(--blue)', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', opacity: couponLoading || !couponInput.trim() ? 0.6 : 1 }}
                      >
                        {couponLoading ? '…' : 'Apply'}
                      </button>
                    </div>
                  )}
                  {couponError && <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 6 }}>{couponError}</div>}
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, marginTop: 4 }}>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ fontWeight: 700, color: '#15803d' }}>{couponApplied.code}</span>
                    <span style={{ color: '#166534', marginLeft: 6 }}>— saves ₹{couponApplied.savings}</span>
                  </div>
                  <button type="button" onClick={removeCoupon} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
                </div>
              )}
            </div>

            <button
              type="submit"
              className="pay-btn"
              disabled={loading}
              data-testid="checkout-pay-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="spinner" size={20} />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard size={20} />
                  Pay ₹{effectivePrice.toLocaleString('en-IN')} Securely
                </>
              )}
            </button>

            <div className="trust-badges">
              <div className="trust-badge">
                <Shield size={16} />
                SSL Encrypted
              </div>
              <div className="trust-badge">
                <CreditCard size={16} />
                Razorpay Secure
              </div>
              <div className="trust-badge">
                <Clock size={16} />
                48hr Delivery
              </div>
            </div>
          </form>
        </div>

        {/* Order Summary */}
        <div className="order-summary">
          <h3 className="summary-title">Order Summary</h3>
          
          <div className="product-item">
            <div className="product-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div className="product-info">
              <div className="product-name">LinkedIn Profile Rewrite</div>
              <div className="product-desc">Recruiter-certified, keyword-optimized</div>
            </div>
          </div>

          <div className="price-breakdown" data-testid="checkout-price-breakdown">
            <div className="price-row">
              <span>Profile Rewrite</span>
              <span data-testid="checkout-price-item">
                {/* pricing.compare_at_price is the generic strike-through value the
                    backend picked — standard_price when an internal offer is live,
                    or market_price when admin is comparing against market rate
                    instead. Renders identically either way; pricing.price is
                    always the amount actually charged in both cases. */}
                {pricing.compare_at_price ? (
                  <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 8 }}>
                    <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>₹{pricing.compare_at_price.toLocaleString('en-IN')}</span>
                    <span style={{ fontWeight: 700 }}>₹{pricing.price.toLocaleString('en-IN')}</span>
                    {/* Same MRP-style "% OFF" badge as the landing page's pricing card */}
                    <span style={{ background: '#dcfce7', color: '#16a34a', fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 100, whiteSpace: 'nowrap' }}>
                      {Math.round((1 - pricing.price / pricing.compare_at_price) * 100)}% OFF
                    </span>
                  </span>
                ) : `₹${pricing.price.toLocaleString('en-IN')}`}
              </span>
            </div>
            {couponApplied && (
              <div className="price-row" style={{ color: '#16a34a' }}>
                <span>Coupon ({couponApplied.code})</span>
                <span>− ₹{couponApplied.savings.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="price-row">
              <span>Recruiter Certification</span>
              <span>Included</span>
            </div>
            <div className="price-row total">
              <span>Total</span>
              <span data-testid="checkout-price-total">₹{effectivePrice.toLocaleString('en-IN')}</span>
            </div>
            <p className="text-xs text-center mt-2" style={{ color: '#64748b' }} data-testid="checkout-provider-note">
              Payment via Razorpay (UPI, cards, netbanking)
            </p>
          </div>

          <div className="features-list">
            <div className="features-title">What&apos;s Included</div>
            <div className="feature-item">
              <CheckCircle />
              Complete profile optimization
            </div>
            <div className="feature-item">
              <CheckCircle />
              Keyword-rich headline & summary
            </div>
            <div className="feature-item">
              <CheckCircle />
              Experience bullet restructuring
            </div>
            <div className="feature-item">
              <CheckCircle />
              Recruiter certification
            </div>
            <div className="feature-item">
              <CheckCircle />
              Delivered within 48 hours
            </div>
          </div>

          <div className="guarantee-box">
            <Shield />
            <div className="guarantee-text">
              <strong>Quality Guarantee</strong>
              If you find better quality elsewhere, we&apos;ll redo it for free.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
